import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Mail, MapPin, FileText, Globe, Upload as UploadIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getCompanySettings,
  updateCompanySettings,
  uploadCompanyLogo,
  type CompanySettings,
} from '@/lib/settings';

const TIMEZONES = [
  'UTC',
  'Europe/Kiev',
  'Europe/Warsaw',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Tokyo',
];
const COMPANY_SIZES = ['1–10', '11–50', '51–200', '201–500', '500+'];
const DATE_FORMATS = ['DD.MM.YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
const CURRENCIES = ['USD', 'EUR', 'UAH', 'PLN', 'GBP'];

// String form values for every text/number field (number kept as string while editing).
type FormState = {
  name: string;
  legalName: string;
  website: string;
  industry: string;
  companySize: string;
  foundedYear: string;
  email: string;
  phone: string;
  supportEmail: string;
  addressStreet: string;
  addressStreet2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  registrationNumber: string;
  taxId: string;
  timezone: string;
  dateFormat: string;
  currency: string;
};

const EMPTY: FormState = {
  name: '',
  legalName: '',
  website: '',
  industry: '',
  companySize: '',
  foundedYear: '',
  email: '',
  phone: '',
  supportEmail: '',
  addressStreet: '',
  addressStreet2: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  registrationNumber: '',
  taxId: '',
  timezone: 'UTC',
  dateFormat: '',
  currency: '',
};

function fromSettings(s: CompanySettings): FormState {
  return {
    name: s.name ?? '',
    legalName: s.legalName ?? '',
    website: s.website ?? '',
    industry: s.industry ?? '',
    companySize: s.companySize ?? '',
    foundedYear: s.foundedYear != null ? String(s.foundedYear) : '',
    email: s.email ?? '',
    phone: s.phone ?? '',
    supportEmail: s.supportEmail ?? '',
    addressStreet: s.addressStreet ?? '',
    addressStreet2: s.addressStreet2 ?? '',
    city: s.city ?? '',
    region: s.region ?? '',
    postalCode: s.postalCode ?? '',
    country: s.country ?? '',
    registrationNumber: s.registrationNumber ?? '',
    taxId: s.taxId ?? '',
    timezone: s.timezone ?? 'UTC',
    dateFormat: s.dateFormat ?? '',
    currency: s.currency ?? '',
  };
}

function SectionCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof Building2;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge">
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <div>
          <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </Card>
  );
}

export function CompanyInfoForm() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [logo, setLogo] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const openPicker = () => fileRef.current?.click();

  const { data: settings } = useQuery<CompanySettings>({
    queryKey: ['companySettings'],
    queryFn: getCompanySettings,
  });

  useEffect(() => {
    if (settings) setForm(fromSettings(settings));
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: updateCompanySettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companySettings'] }),
  });
  const uploadLogoMutation = useMutation({
    mutationFn: uploadCompanyLogo,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companySettings'] }),
  });
  const saving = updateMutation.isPending || uploadLogoMutation.isPending;

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const logoPreview = useMemo(() => {
    if (logo) return URL.createObjectURL(logo);
    if (!settings?.logoUrl) return null;
    return settings.logoUrl.startsWith('http')
      ? settings.logoUrl
      : `${window.location.origin}${settings.logoUrl}`;
  }, [logo, settings?.logoUrl]);

  const handleSave = async () => {
    try {
      if (logo) await uploadLogoMutation.mutateAsync(logo);
      await updateMutation.mutateAsync({
        name: form.name,
        website: form.website || undefined,
        timezone: form.timezone,
        legalName: form.legalName,
        industry: form.industry,
        companySize: form.companySize,
        foundedYear: form.foundedYear ? Number(form.foundedYear) : null,
        email: form.email,
        phone: form.phone,
        supportEmail: form.supportEmail,
        addressStreet: form.addressStreet,
        addressStreet2: form.addressStreet2,
        city: form.city,
        region: form.region,
        postalCode: form.postalCode,
        country: form.country,
        registrationNumber: form.registrationNumber,
        taxId: form.taxId,
        dateFormat: form.dateFormat,
        currency: form.currency,
      });
      setLogo(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save company info:', error);
    }
  };

  const text = (k: keyof FormState, label: string, props?: { placeholder?: string; type?: string; wide?: boolean }) => (
    <div className={props?.wide ? 'space-y-1.5 sm:col-span-2' : 'space-y-1.5'}>
      <Label htmlFor={k}>{label}</Label>
      <Input
        id={k}
        type={props?.type ?? 'text'}
        placeholder={props?.placeholder}
        value={form[k]}
        onChange={set(k)}
      />
    </div>
  );

  const select = (k: keyof FormState, label: string, options: string[], placeholder: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={k}>{label}</Label>
      <Select value={form[k] || undefined} onValueChange={(v) => setForm((f) => ({ ...f, [k]: v }))}>
        <SelectTrigger id={k}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-6 pb-24">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Company Information</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your company profile, address and legal details — used across the app, exports and emails.
        </p>
      </div>

      <SectionCard icon={Building2} title="Identity" description="How your company shows up across HyggeOS">
        <div className="mb-6 flex items-center gap-4">
          <button
            type="button"
            onClick={openPicker}
            aria-label="Upload company logo"
            className="group relative flex h-[76px] w-[76px] shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-muted-foreground/30 bg-muted/40 transition-colors hover:border-hygge/50"
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Company logo" className="h-full w-full object-cover" />
            ) : (
              <UploadIcon className="h-6 w-6 text-muted-foreground transition-colors group-hover:text-hygge" />
            )}
            {logoPreview && (
              <span className="absolute inset-0 hidden items-center justify-center bg-black/45 group-hover:flex">
                <UploadIcon className="h-5 w-5 text-white" />
              </span>
            )}
          </button>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={openPicker}
                className="inline-flex items-center gap-2 rounded-full bg-hygge/10 px-4 py-2 text-sm font-medium text-hygge transition-colors hover:bg-hygge/15"
              >
                <UploadIcon className="h-4 w-4" />
                {logo || settings?.logoUrl ? 'Change logo' : 'Upload logo'}
              </button>
              {logo && (
                <span className="max-w-[180px] truncate text-xs text-muted-foreground">
                  {logo.name}
                </span>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              PNG or JPG, square works best · max 5 MB
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setLogo(f);
            }}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {text('name', 'Company name', { placeholder: 'Company', wide: true })}
          {text('legalName', 'Legal name', { placeholder: 'Hygge Software LLC' })}
          {text('website', 'Website', { type: 'url', placeholder: 'https://hygge.software' })}
          {text('industry', 'Industry', { placeholder: 'Software' })}
          {select('companySize', 'Company size', COMPANY_SIZES, 'Select size')}
          {text('foundedYear', 'Founded', { type: 'number', placeholder: '2018' })}
        </div>
      </SectionCard>

      <SectionCard icon={Mail} title="Contact" description="Where people can reach the company">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {text('email', 'Primary email', { type: 'email', placeholder: 'hello@hygge.software' })}
          {text('phone', 'Phone', { placeholder: '+380 44 000 0000' })}
          {text('supportEmail', 'Support email', { type: 'email', placeholder: 'support@hygge.software' })}
        </div>
      </SectionCard>

      <SectionCard icon={MapPin} title="Registered address" description="Legal / billing address">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {text('addressStreet', 'Street address', { placeholder: '12 Khreshchatyk St', wide: true })}
          {text('addressStreet2', 'Apt, suite, floor', { placeholder: 'Floor 4' })}
          {text('city', 'City', { placeholder: 'Kyiv' })}
          {text('region', 'Region / State', { placeholder: 'Kyiv Oblast' })}
          {text('postalCode', 'Postal code', { placeholder: '01001' })}
          {text('country', 'Country', { placeholder: 'Ukraine' })}
        </div>
      </SectionCard>

      <SectionCard icon={FileText} title="Legal & tax" description="Registration & tax identifiers">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {text('registrationNumber', 'Registration number', { placeholder: '12345678' })}
          {text('taxId', 'VAT / Tax ID', { placeholder: 'UA1234567890' })}
        </div>
      </SectionCard>

      <SectionCard icon={Globe} title="Localization" description="Formats used in exports & emails">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {select('timezone', 'Timezone', TIMEZONES, 'Select timezone')}
          {select('dateFormat', 'Date format', DATE_FORMATS, 'Select format')}
          {select('currency', 'Default currency', CURRENCIES, 'Select currency')}
        </div>
      </SectionCard>

      <div className="flex justify-end pt-1">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
